/**
 * The access token, held in memory only.
 *
 * **Nothing about the session is persisted by this module.** The refresh token is an
 * HttpOnly cookie the browser manages and no script can read — including this one —
 * and the access token lives in a module variable that dies with the tab.
 *
 * That is the whole point of the change: anything in `localStorage` is readable by any
 * script that runs on the page, including one that arrives through a dependency, and a
 * refresh token is a fourteen-day credential. A cookie the script cannot read does not
 * prevent XSS; it stops the XSS walking away with two weeks of access to an agency's
 * customer records.
 *
 * Surviving a page reload is the cookie's job: the app asks
 * `/api/auth/token/refresh/` for a fresh access token on boot, and the browser
 * attaches the cookie for it.
 */
let accessToken: string | null = null;

export function readAccessToken(): string | null {
  return accessToken;
}

export function writeAccessToken(token: string): void {
  accessToken = token;
}

export function clearAccessToken(): void {
  accessToken = null;
}

/**
 * Remove anything a previous version of this app left in `localStorage`.
 *
 * Someone who used Kapitan before this change still has a refresh token sitting in
 * their browser's storage. It is useless now, but leaving a credential lying about
 * because it stopped being read would be a poor reason to keep it.
 */
export function purgeLegacyTokenStorage(): void {
  try {
    localStorage.removeItem("kapitan.access");
    localStorage.removeItem("kapitan.refresh");
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). There is nothing to
    // clean up in that case, and failing to boot over it would be absurd.
  }
}
